import { Opportunity as OpportunityEntity } from "@entities";
import { opportunitySeatsAreFull } from "@shared/helpers/opportunity-occupancy";

type PartnerOrgOpportunityStatusInput = {
  isDeleted?: boolean | null;
  isClosed?: boolean | null;
  isFull?: boolean | null;
  status?: string | null;
};

/**
 * משימות פעילות לארגון שותף: פתוחות + לא טופלו.
 * לא כולל ארכיון, טופלו, מלאות ובקשות סיוע.
 */
export function partnerOrgActiveOpportunitiesWhere() {
  return {
    parentOpportunityId: null,
    isDeleted: false,
    isClosed: false,
    status: { $ne: "AID_REQUEST" as const },
    $and: [OpportunityEntity.isInNeedOfParticipants({ spaces: 1 })],
  };
}

/** עלים פתוחים לספירה בקטלוג. תואם למגירה: לא אבות קבוצה, לא מחוקים, לא סגורים. */
export function partnerOrgOpenLeavesCountWhere(orgIds: string[]) {
  return {
    partnerOrganizationId: { $in: orgIds },
    isOpportunityGroupParent: false,
    isDeleted: false,
    isClosed: false,
  };
}

/**
 * מופעי עלים של ארגון שותף, כולל בקשות סיוע.
 * כולל עלים שירשו את הארגון מהאב, כמו במענה חסר.
 */
function partnerOrgLeafOpportunitiesFilter(orgId: string) {
  return {
    isOpportunityGroupParent: false,
    isDeleted: false,
    isClosed: false,
    $or: [
      { partnerOrganizationId: orgId },
      { parentOpportunity: { $is: { partnerOrganizationId: orgId } } },
    ],
  };
}

export function partnerOrgLeafOpportunitiesWhere(orgId: string) {
  return OpportunityEntity.hyperFilter(partnerOrgLeafOpportunitiesFilter(orgId));
}

export type PartnerOrgLeafChip = "open" | "empty" | "soon" | "aid";

/** מופעי עלים של הארגון, מסוננים לפי צ'יפ בשרת. */
export function partnerOrgLeafOpportunitiesWhereForChip(
  orgId: string,
  chip: PartnerOrgLeafChip,
  now = Date.now(),
) {
  const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;
  const chipWhere =
    chip === "aid"
      ? { status: "AID_REQUEST" as const }
      : chip === "open"
        ? { status: { $ne: "AID_REQUEST" as const } }
        : chip === "empty"
          ? {
              participantsCount: 0,
              status: { $ne: "AID_REQUEST" as const },
            }
          : {
              status: { $ne: "AID_REQUEST" as const },
              startDate: {
                $gte: new Date(now),
                $lte: new Date(weekFromNow),
              },
            };

  const where = OpportunityEntity.hyperFilter({
    ...partnerOrgLeafOpportunitiesFilter(orgId),
    ...chipWhere,
  });
  if (chip === "aid") return where;
  return {
    $and: [where, OpportunityEntity.isInNeedOfParticipants({ spaces: 1 })],
  };
}

/** Row or parent is linked to one of the selected partner orgs. */
export function opportunitiesWithPartnerOrganizationWhere(ids: string[]) {
  return OpportunityEntity.hyperFilter({
    $or: [
      { partnerOrganizationId: { $in: ids } },
      { parentOpportunity: { $is: { partnerOrganizationId: { $in: ids } } } },
    ],
  });
}

const emptyTargetOrganizationName = {
  $or: [
    { targetOrganizationName: null },
    { targetOrganizationName: "" },
  ],
};

/** Entity-filter shape so callers can combine with other conditions in one hyperFilter. */
export function opportunitiesWithoutPartnerOrganizationFilter() {
  return {
    $and: [
      { partnerOrganizationId: null },
      emptyTargetOrganizationName,
      {
        $or: [
          { parentOpportunityId: null },
          {
            parentOpportunity: {
              $is: {
                partnerOrganizationId: null,
                ...emptyTargetOrganizationName,
              },
            },
          },
        ],
      },
    ],
  };
}

/** No partner FK, no org/authority text, and none inherited from the parent. */
export function opportunitiesWithoutPartnerOrganizationWhere() {
  return OpportunityEntity.hyperFilter(
    opportunitiesWithoutPartnerOrganizationFilter() as Parameters<
      typeof OpportunityEntity.hyperFilter
    >[0],
  );
}

export function opportunityMissingSeats(opportunity: {
  participantsCount?: number | null;
  amount?: number | null;
}): number {
  const assigned = opportunity.participantsCount ?? 0;
  const slots = opportunity.amount ?? 0;
  return Math.max(0, slots - assigned);
}

type PartnerOrgLeafRow = PartnerOrgOpportunityStatusInput & {
  startDate?: Date | string | null;
  participantsCount?: number | null;
  amount?: number | null;
};

export function filterPartnerOrgLeaves<T extends PartnerOrgLeafRow>(
  rows: T[],
  chip: PartnerOrgLeafChip,
  now = Date.now(),
): T[] {
  const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;
  return rows.filter((row) => {
    if (chip === "aid") {
      return row.status === "AID_REQUEST" && !row.isClosed;
    }
    if (row.isClosed || row.status === "AID_REQUEST") return false;
    if (opportunitySeatsAreFull(row)) {
      return false;
    }
    if (chip === "open") return row.isFull !== true;
    if (chip === "empty") return (row.participantsCount ?? 0) === 0;
    if (chip === "soon") {
      if (!row.startDate) return false;
      const start = new Date(row.startDate).getTime();
      return !Number.isNaN(start) && start >= now && start <= weekFromNow;
    }
    return true;
  });
}

export function sortPartnerOrgLeaves<T extends PartnerOrgLeafRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const aidA = a.status === "AID_REQUEST" ? 0 : 1;
    const aidB = b.status === "AID_REQUEST" ? 0 : 1;
    if (aidA !== aidB) return aidA - aidB;

    const emptyA = (a.participantsCount ?? 0) === 0 ? 0 : 1;
    const emptyB = (b.participantsCount ?? 0) === 0 ? 0 : 1;
    if (emptyA !== emptyB) return emptyA - emptyB;

    const missA = opportunityMissingSeats(a);
    const missB = opportunityMissingSeats(b);
    const startA = a.startDate ? new Date(a.startDate).getTime() : Number.POSITIVE_INFINITY;
    const startB = b.startDate ? new Date(b.startDate).getTime() : Number.POSITIVE_INFINITY;
    const validA = !Number.isNaN(startA);
    const validB = !Number.isNaN(startB);

    if (missA > 0 && missB > 0 && validA && validB && startA !== startB) {
      return startA - startB;
    }
    if (missA !== missB) return missB - missA;
    if (validA && validB && startA !== startB) return startA - startB;
    return 0;
  });
}

/** סטטוס תצוגה למשימה מקושרת לארגון שותף - תואם ללשוניות טבלת המשימות */
export function getPartnerOrgOpportunityListStatus(
  opportunity: PartnerOrgOpportunityStatusInput & {
    participantsCount?: number | null;
    amount?: number | null;
  },
): string {
  if (opportunity.isDeleted) return "בארכיון";
  if (opportunity.status === "AID_REQUEST") return "בקשת סיוע";
  if (opportunity.isClosed) return "טופלה";
  if (opportunitySeatsAreFull(opportunity)) return "מלאה";
  return "פתוחה";
}

type PartnerOrgOpportunityRequesterInput = {
  associationRequestedId?: string | null;
  associationRequested?: {
    user?: {
      name?: string | null;
      phoneNumber?: string | null;
    } | null;
  } | null;
};

/** פרטי מבקש/ת הסיוע — כמו בעמודת «מבקש סיוע» בטבלת המשימות */
export function formatPartnerOrgOpportunityRequester(
  opportunity: PartnerOrgOpportunityRequesterInput,
): string {
  const user = opportunity.associationRequested?.user;
  if (!user && !opportunity.associationRequestedId) return "-";

  const name = (user?.name ?? "").trim();
  const phone = (user?.phoneNumber ?? "").trim();

  if (name && phone) return `${name} · ${phone}`;
  if (name) return name;
  if (phone) return phone;
  return "-";
}

export function formatPartnerOrgOpportunityParticipants(
  participantsCount: number | null | undefined,
  amount: number | null | undefined,
): string {
  const count = participantsCount ?? 0;
  const total = amount ?? 1;
  return `${count} מתוך ${total} משתתפים`;
}
